import {
  InterventionGroupDrawer,
  type EnrichedPatient
} from '../components/InterventionGroupDrawer/InterventionGroupDrawer';
import AddTreatmentPlanModal from '../components/AddTreatmentPlanModal/AddTreatmentPlanModal';
import type { TreatmentPlanRolloutSelection } from '../services/treatmentPlanRolloutService';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useScenarioLock } from '../hooks/useScenarioLock';
import { YearPlanKpi } from '../components/YearPlanKpi/YearPlanKpi';
import { useYearPlanTarget } from '../hooks/useYearPlanTarget';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '../components/GanttChart/UnsavedChangesModal';
import { useHasPermission } from '../hooks/useHasPermission';
import { PERMISSIONS } from '../constants/permissions';
import { GanttChart } from '../components/GanttChart/GanttChart';
import { RecalculateWeekModal } from '../components/GanttChart/WeekKpiCell/RecalculateWeekModal';
import { CalculateWeeksModal } from '../components/GanttChart/CalculateWeeksModal';
import { useStaff } from '../hooks/useStaff';
import { useBedContext } from '../hooks/useBedContext';
import { usePatientRooms } from '../hooks/usePatientRooms';
import { useYearPlan } from '../hooks/useYearPlan';
import { useWeekCalculation } from '../hooks/useWeekCalculation';
import { useWeekRangeCalculation } from '../hooks/useWeekRangeCalculation';
import { useWeekCalculationStatuses } from '../hooks/useWeekCalculationStatuses';
import { useWeekCalendarModal } from '../hooks/useWeekCalendarModal';
import { buildWeekUnscheduledSummary } from '../services/schedulingResultService';
import type { WeekUnscheduledSummary } from '../services/schedulingResultService';
import { fetchWeekSchedulingResult } from '../services/weekSchedulingResultService';
import { IndividualCalendarModal } from '../components/WeekOverview/IndividualCalendarModal/IndividualCalendarModal';
import { UnscheduledInterventionsModal } from '../components/WeekOverview/UnscheduledInterventionsModal/UnscheduledInterventionsModal';
import { Modal } from '../components/UI';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectHasPendingChanges, setEditMode } from '../store/application/applicationSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { buildYearPlanErrorDialog } from '../services/yearPlanErrorService';
import { selectPatientsById } from '../store/patients/patientsSlice';
import styles from './YearOverviewPage.module.css';
import { YearPicker } from 'qampo-ui-library';
import { formatDate, getCurrentIsoWeek } from '../utils/dateUtils';

export const YearOverviewPage = () => {
  const { t, i18n } = useTranslation();
  const { locationId } = useParams<{ locationId: string }>();
  const [selectedTreatmentPlanId, setSelectedTreatmentPlanId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [addTreatmentPlanBatchDialog, setAddTreatmentPlanBatchDialog] = useState<{
    treatmentPlanTemplateId: string;
    weekNumber: number;
    mode: 'single' | 'series';
  } | null>(null);
  const [addPatientOnOpen, setAddPatientOnOpen] = useState(false);

  const dispatch = useAppDispatch();
  const patientsById = useAppSelector(selectPatientsById);
  // Plan edits and a typed-in year target, the same signal the toolbar reads.
  const hasPendingChanges = useAppSelector(selectHasPendingChanges);

  const {
    yearPlan,
    treatmentPlanTemplates,
    selectedYear,
    setSelectedYear,
    scenarioId,
    locationName,
    loading,
    changeSummary,
    handleAddTreatmentPlan,
    handleDeleteTreatmentPlan,
    handleDeleteTreatmentPlans,
    handleRenameTreatmentPlans,
    handleMoveTreatmentPlanRow,
    updateCapacity,
    handleMoveIntervention,
    handleMoveStart,
    handleMoveDiscard,
    handleMoveAccept,
    handleDiscardAll,
    handleAcceptAll,
    refreshYearPlan,
    fetchTemplates,
    clearTemplates
  } = useYearPlan(locationId);

  // The target rides the plan's edit session, so the same right that opens
  // edit mode decides whether its field is offered.
  const canEditTarget = useHasPermission(PERMISSIONS.YearPlanWrite);
  const { target, setTarget, saveTarget, discardTarget } = useYearPlanTarget(
    locationId,
    selectedYear
  );
  // The staff roster feeds the week KPI's capacity thresholds (REH-330). Until
  // this location's roster has settled, the Gantt gets no staff and the KPI
  // omits capacity rather than judging planned hours against a guess.
  const { staff, isLoadedForLocation: isStaffLoaded } = useStaff(locationId ?? null);
  // The rooms are the bed chip's ceiling: a week planning more admitted patients
  // than the location has rooms, or booking patients whose room needs the rooms
  // cannot meet, reads as overbooked, with no solve needed to say so.
  const { patientRooms } = usePatientRooms(locationId ?? null);
  const beds = useBedContext(patientRooms);
  // The renewal loop found the lock gone, so the edit session cannot continue.
  // Edit mode comes down first: everything the toolbar offers would target a
  // lock this tab no longer holds, and it must stay unavailable even if the
  // refetch below fails. No `releaseLock` — the hook has already dropped the
  // held key, and a release would hand back whoever's lock it is now.
  const handleLockLost = useCallback(() => {
    dispatch(setEditMode(false));
    clearTemplates();
    void handleDiscardAll().catch((error: unknown) => {
      console.error('Failed to reload the plan after losing the lock', error);
    });
  }, [dispatch, clearTemplates, handleDiscardAll]);

  const { isLocked, lockedByCurrentUser, lockedByName, acquireLock, releaseLock } = useScenarioLock(
    locationId,
    scenarioId ?? undefined,
    handleLockLost
  );
  const {
    statuses: weekCalculations,
    markWeekCalculating,
    refresh: retryCalculationStatuses
  } = useWeekCalculationStatuses(locationId, scenarioId ?? undefined);
  const {
    pendingWeekNumber,
    isTriggering,
    requestCalculation,
    cancelCalculation,
    confirmCalculation
  } = useWeekCalculation(locationId, scenarioId ?? undefined, markWeekCalculating);
  const {
    isDialogOpen: isCalculateWeeksOpen,
    isTriggering: isTriggeringWeeks,
    openDialog: openCalculateWeeks,
    closeDialog: closeCalculateWeeks,
    confirmCalculation: confirmCalculateWeeks
  } = useWeekRangeCalculation(locationId, scenarioId ?? undefined, markWeekCalculating);
  const {
    weekData: calendarWeekData,
    isLoading: isCalendarLoading,
    filters: calendarFilters,
    setFilters: setCalendarFilters,
    openCalendar,
    closeCalendar
  } = useWeekCalendarModal(locationId, scenarioId ?? undefined);

  // The week whose unplanned list is open, together with that list. The header's counts arrive on the
  // week statuses it already polls, so the full solver result — around 915 kB on a loaded week — is
  // read only for the one week a planner actually opens, and only when they open it.
  const [openUnscheduled, setOpenUnscheduled] = useState<{
    readonly weekNumber: number;
    readonly summary: WeekUnscheduledSummary;
  } | null>(null);

  const handleOpenUnscheduled = useCallback(
    (weekNumber: number) => {
      if (!locationId || !scenarioId) return;
      void (async () => {
        const outcome = await fetchWeekSchedulingResult({ locationId, scenarioId, weekNumber });
        if (outcome.kind !== 'planned') {
          dispatch(showError({ message: t('fetch-error') }));
          return;
        }
        try {
          const summary = buildWeekUnscheduledSummary(outcome.response);
          // The count that produced the tag came from the last status poll, so a run that finished
          // in between can leave nothing to list. Opening a dialog headed "unplanned interventions"
          // above an empty list would be worse than the click appearing to do nothing.
          if (summary.interventions.length === 0) {
            console.debug(`Week ${weekNumber} has nothing unplanned in its current result`);
            return;
          }
          setOpenUnscheduled({ weekNumber, summary });
        } catch (error: unknown) {
          // `buildWeekUnscheduledSummary` can throw on a payload that got past the service's shape
          // check, and an unhandled rejection here would take the page down with it.
          console.error(`Failed to read unscheduled interventions for week ${weekNumber}:`, error);
          dispatch(showError({ message: t('fetch-error') }));
        }
      })();
    },
    [locationId, scenarioId, dispatch, t]
  );

  const handleCloseUnscheduled = useCallback(() => {
    setOpenUnscheduled(null);
  }, []);

  const handleOpenCalendar = useCallback(
    (weekNumber: number) => {
      openCalendar(selectedYear, weekNumber);
    },
    [openCalendar, selectedYear]
  );

  const acquireLockAndRefresh = useCallback(async (): Promise<boolean> => {
    const acquired = await acquireLock();
    if (acquired) {
      await refreshYearPlan();
      await fetchTemplates();
    }
    return acquired;
  }, [acquireLock, refreshYearPlan, fetchTemplates]);

  const handleAddTreatmentPlanBatch = useCallback(
    (treatmentPlanTemplateId: string, weekNumber: number, mode: 'single' | 'series') => {
      setAddTreatmentPlanBatchDialog({ treatmentPlanTemplateId, weekNumber, mode });
    },
    []
  );

  const handleCloseBatchDialog = useCallback(() => {
    setAddTreatmentPlanBatchDialog(null);
  }, []);

  const handleConfirmBatchDialog = useCallback(
    (selection: TreatmentPlanRolloutSelection) => {
      if (!addTreatmentPlanBatchDialog) return;
      const template = treatmentPlanTemplates.find(
        (t) => t.id === addTreatmentPlanBatchDialog.treatmentPlanTemplateId
      );
      if (template) {
        handleAddTreatmentPlan(template, selection);
      }
      setAddTreatmentPlanBatchDialog(null);
    },
    [addTreatmentPlanBatchDialog, treatmentPlanTemplates, handleAddTreatmentPlan]
  );

  const handleClose = useCallback(() => {
    setSelectedTreatmentPlanId(null);
    setSelectedGroupId(null);
  }, []);

  const handleGroupClick = useCallback((treatmentPlanId: string, groupId: string) => {
    setSelectedTreatmentPlanId(treatmentPlanId);
    setSelectedGroupId(groupId);
  }, []);

  const handleAddPatient = useCallback((treatmentPlanId: string, groupId: string) => {
    setSelectedTreatmentPlanId(treatmentPlanId);
    setSelectedGroupId(groupId);
    setAddPatientOnOpen(true);
  }, []);

  const handleDiscardAllAndRelease = useCallback(async () => {
    try {
      discardTarget();
      await handleDiscardAll();
      await releaseLock();
      clearTemplates();
    } catch (e) {
      dispatch(showError(buildYearPlanErrorDialog(e, t, t('error-discard-treatment-plans'))));
      throw e;
    }
  }, [discardTarget, handleDiscardAll, releaseLock, clearTemplates, t, dispatch]);

  // Leaving mid-edit throws the local edits away, so the lock has to go back and
  // edit mode has to come down before the navigation is allowed to resume.
  //
  // Deliberately not the discard path: that re-fetches the plan from the server
  // only to resync local state the unmount is about to drop anyway. The guard
  // resumes navigation even if this rejects, and the lock-hook cleanup releases
  // on unmount, so a discard failure would cost a wasted request and an error
  // dialog for nothing. `releaseLock` swallows its own errors, so this cannot
  // reject.
  const handleLeaveWithoutSaving = useCallback(async (): Promise<void> => {
    await releaseLock();
    clearTemplates();
    dispatch(setEditMode(false));
  }, [releaseLock, clearTemplates, dispatch]);

  // Two independent sources of unsaved work, each owning its own flag in the
  // store and read together through one selector. Both writing a single flag
  // cannot work: useYearPlan writes false when a plan edit is reverted, which
  // would switch the guard off with a typed-in target still pending.
  const { isLeaveBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(
    hasPendingChanges,
    handleLeaveWithoutSaving
  );

  // Edit mode is global state, so a page that goes away while still in it would
  // bring the Gantt back up in edit mode over a lock that has been released.
  // Keyed to the location because switching location keeps this route mounted:
  // with no unsaved changes there is no prompt either, so without the location
  // key the new location would come up in edit mode over a lock that was never
  // acquired for it.
  useEffect(() => {
    return () => {
      dispatch(setEditMode(false));
    };
  }, [dispatch, locationId]);

  // One rule for both the single-plan delete and the whole-row removal: the
  // drawer cannot be left showing a plan that has just been taken away.
  const closeDrawerIfShowing = useCallback(
    (removedTreatmentPlanIds: string[]) => {
      if (selectedTreatmentPlanId && removedTreatmentPlanIds.includes(selectedTreatmentPlanId)) {
        setSelectedTreatmentPlanId(null);
        setSelectedGroupId(null);
      }
    },
    [selectedTreatmentPlanId]
  );

  const handleDeleteAndCloseDrawer = useCallback(
    (treatmentPlanId: string) => {
      handleDeleteTreatmentPlan(treatmentPlanId);
      closeDrawerIfShowing([treatmentPlanId]);
    },
    [handleDeleteTreatmentPlan, closeDrawerIfShowing]
  );

  const handleDeleteRowAndCloseDrawer = useCallback(
    (treatmentPlanIds: string[]) => {
      handleDeleteTreatmentPlans(treatmentPlanIds);
      closeDrawerIfShowing(treatmentPlanIds);
    },
    [handleDeleteTreatmentPlans, closeDrawerIfShowing]
  );

  // No message of its own: handleAcceptAll now reports the failure, and it can name the lock
  // holder where this could only ever say "something went wrong". What stays here is the flow
  // control — the lock and the loaded templates survive a failed save, so the user can retry.
  const handleAcceptAllAndRelease = useCallback(async () => {
    // Before the plan save: if that rejects, the target has still been stored,
    // which is the safer failure. Losing a government figure the planner just
    // typed because an unrelated plan edit was refused would be worse.
    // saveTarget reports its own refusal to the planner; false only decides what
    // happens to edit mode below.
    const isTargetSaved = saveTarget();
    await handleAcceptAll();

    // The plan is saved either way. A refused target write, though, has to leave
    // edit mode and the lock standing, because the field only exists in edit
    // mode: dropping out of it would take the unsaved figure off screen with no
    // way back to it. Throwing is how the toolbar is told to stay put.
    if (!isTargetSaved) throw new Error('The year target write was refused');

    await releaseLock();
    clearTemplates();
  }, [saveTarget, handleAcceptAll, releaseLock, clearTemplates]);

  // A plan carried over from the previous year opens too, but only to look at:
  // it belongs to that year's plan.
  const selectedCarriedOverPlan = useMemo(
    () =>
      yearPlan?.carriedOverPlans?.treatmentPlans.find((p) => p.id === selectedTreatmentPlanId),
    [yearPlan, selectedTreatmentPlanId]
  );
  const selectedTreatmentPlan = useMemo(
    () =>
      yearPlan?.treatmentPlans.find((p) => p.id === selectedTreatmentPlanId) ??
      selectedCarriedOverPlan,
    [yearPlan, selectedTreatmentPlanId, selectedCarriedOverPlan]
  );

  const selectedGroup = useMemo(() => {
    if (!selectedTreatmentPlan || !selectedGroupId) return undefined;
    for (const week of selectedTreatmentPlan.weeks) {
      const group = (week.groups ?? []).find((g) => g.id === selectedGroupId);
      if (group) return group;
    }
    return undefined;
  }, [selectedTreatmentPlan, selectedGroupId]);

  const selectedPatients = useMemo<EnrichedPatient[]>(() => {
    if (!selectedGroup) return [];
    return (selectedGroup.assignedPatients ?? []).map((p) => ({
      ...p,
      name: patientsById[p.patientId]?.name
    }));
  }, [selectedGroup, patientsById]);

  const ganttChart = yearPlan ? (
    <GanttChart
      yearPlan={yearPlan}
      year={selectedYear}
      changeSummary={changeSummary}
      treatmentPlanTemplates={treatmentPlanTemplates}
      isLocked={isLocked}
      lockedByCurrentUser={lockedByCurrentUser}
      lockedByName={lockedByName}
      onAcquireLock={acquireLockAndRefresh}
      onGroupClick={handleGroupClick}
      onAddTreatmentPlan={handleAddTreatmentPlan}
      onMoveIntervention={handleMoveIntervention}
      onMoveStart={handleMoveStart}
      onMoveDiscard={handleMoveDiscard}
      onMoveAccept={handleMoveAccept}
      onDiscardAll={handleDiscardAllAndRelease}
      onAcceptAll={handleAcceptAllAndRelease}
      onAddTreatmentPlanBatch={handleAddTreatmentPlanBatch}
      onDeleteTreatmentPlan={handleDeleteAndCloseDrawer}
      onDeleteTreatmentPlans={handleDeleteRowAndCloseDrawer}
      onRenameTreatmentPlans={handleRenameTreatmentPlans}
      onMoveTreatmentPlanRow={handleMoveTreatmentPlanRow}
      onAddPatient={handleAddPatient}
      onWeekStatusClick={requestCalculation}
      onOpenCalendar={handleOpenCalendar}
      onRetryCalculationStatuses={retryCalculationStatuses}
      onCalculateWeeks={openCalculateWeeks}
      weekCalculations={weekCalculations}
      onOpenUnscheduled={handleOpenUnscheduled}
      staff={isStaffLoaded ? staff : undefined}
      beds={beds}
    />
  ) : null;

  return (
    <main className={styles.yearOverview}>
      <div className={styles.titleRow}>
      <h1 className={styles.heading}>
        {t('nav-year-overview')}{' '}
        <div className={styles.yearPicker}>
          <YearPicker
            value={selectedYear}
            onChange={lockedByCurrentUser ? () => {} : setSelectedYear}
            styles={lockedByCurrentUser ? { root: { pointerEvents: 'none', opacity: 0.6 } } : undefined}
            theme={{
              inputBackground: 'transparent',
              popupBackground: 'var(--color-surface-primary)',
              yearSelectedBackground: 'var(--color-interaction-primary)',
              yearSelectedBorderRadius: 'var(--radius-regular)'
            }}
          />
        </div>
      </h1>
      {!loading && changeSummary && locationId && (
        <YearPlanKpi
          money={changeSummary.currentMoney}
          serverMoney={changeSummary.serverMoney}
          target={target}
          year={selectedYear}
          isEditMode={lockedByCurrentUser}
          canEditTarget={canEditTarget}
          onTargetChange={setTarget}
        />
      )}
      </div>
      {locationName && (
        <div className={styles.subHeading}>
          <p className={styles.locationName}>{locationName}</p>
          {yearPlan?.lastModifiedDate && (
            <p className={styles.lastModified}>
              {t('last-modified')} {formatDate(yearPlan.lastModifiedDate, i18n.language)}
            </p>
          )}
        </div>
      )}
      {loading ? <p>{t('gantt-loading')}</p> : ganttChart}

      {/* Loading shell: instant feedback on the header click, and it blocks
          the page so no second modal can open under the settling calendar */}
      {isCalendarLoading && (
        <Modal title={t('week-overview-modal-title')} onClose={closeCalendar}>
          <p>{t('week-overview-calendar-loading')}</p>
        </Modal>
      )}

      {openUnscheduled && (
        <UnscheduledInterventionsModal
          interventions={openUnscheduled.summary.interventions}
          patientNameById={openUnscheduled.summary.patientNameById}
          weekNumber={openUnscheduled.weekNumber}
          onClose={handleCloseUnscheduled}
        />
      )}

      {calendarWeekData && (
        <IndividualCalendarModal
          weekData={calendarWeekData}
          filters={calendarFilters}
          onFiltersChange={setCalendarFilters}
          onClose={closeCalendar}
        />
      )}

      {isLeaveBlocked && (
        <UnsavedChangesModal onConfirm={confirmLeave} onCancel={cancelLeave} />
      )}

      {/* Not while the leave prompt is up: a browser Back can raise that prompt
          over this modal, and two stacked dialogs both answer the same Escape. */}
      {pendingWeekNumber !== null && !isLeaveBlocked && (
        <RecalculateWeekModal
          weekNumber={pendingWeekNumber}
          isTriggering={isTriggering}
          onConfirm={confirmCalculation}
          onClose={cancelCalculation}
        />
      )}

      {/* Same rule as the single-week dialog: never stacked under the leave prompt. The range opens
          on the current week in the current year, and on the year's first week otherwise. */}
      {isCalculateWeeksOpen && yearPlan && !isLeaveBlocked && (
        <CalculateWeeksModal
          weeks={yearPlan.weeks}
          initialWeekNumber={
            selectedYear === new Date().getFullYear() ? getCurrentIsoWeek() : 1
          }
          isTriggering={isTriggeringWeeks}
          onConfirm={confirmCalculateWeeks}
          onClose={closeCalculateWeeks}
        />
      )}

      <AddTreatmentPlanModal
        treatmentPlanTemplateId={addTreatmentPlanBatchDialog?.treatmentPlanTemplateId ?? ''}
        weekNumber={addTreatmentPlanBatchDialog?.weekNumber ?? 1}
        mode={addTreatmentPlanBatchDialog?.mode ?? 'single'}
        weeks={yearPlan?.weeks ?? []}
        treatmentPlanTemplates={treatmentPlanTemplates}
        isOpen={addTreatmentPlanBatchDialog !== null}
        onClose={handleCloseBatchDialog}
        onConfirm={handleConfirmBatchDialog}
      />
      <InterventionGroupDrawer
        isOpen={selectedTreatmentPlanId !== null}
        onClose={handleClose}
        treatmentPlan={selectedTreatmentPlan}
        selectedGroup={selectedGroup}
        patients={selectedPatients}
        weeks={yearPlan?.weeks}
        isEditMode={lockedByCurrentUser}
        isReadOnly={selectedCarriedOverPlan !== undefined}
        carriedOverFromYear={
          selectedCarriedOverPlan ? yearPlan?.carriedOverPlans?.year : undefined
        }
        onCapacityChange={(capacity, groupIds) => {
          if (!selectedTreatmentPlanId) return;
          updateCapacity(selectedTreatmentPlanId, capacity, groupIds);
        }}
        onSelectGroup={setSelectedGroupId}
        patientsById={patientsById}
        addPatientOnOpen={addPatientOnOpen}
        onAddPatientHandled={() => setAddPatientOnOpen(false)}
        locationId={locationId}
        scenarioId={scenarioId ?? undefined}
        onPatientRemoved={() => void refreshYearPlan()}
        onPatientAdded={() => void refreshYearPlan()}
      />
    </main>
  );
};
